import { ErrorBoundary } from '@sentry/react';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense } from 'react';
import { metricsAtom } from '@/atoms/metric';
import { playerMetricsAtom } from '@/atoms/metric/playerMetrics';
import { playerComparisonIsLoadingAtom } from '@/atoms/queries/player/playerComparisonRadar';
import {
  playerLeagueAverageIsLoadingAtom,
  unwrappedPlayerRenderedComparisonMinutesPlayedAtom,
  unwrappedPlayerRenderedComparisonMinutesPlayed360Atom,
} from '@/atoms/queries/player/playerLeagueAverageRadar';
import {
  unwrappedPlayerAggsMinutesPlayedAtom,
  unwrappedPlayerAggsMinutesPlayed360Atom,
} from '@/atoms/queries/player/playerMinsPlayed';
import { loadablePlayerRadarDataAtom } from '@/atoms/queries/player/playerRadar';
import { radarTemplatesAtom } from '@/atoms/queries/userConfigs';
import {
  radarTableComparisonMetricPercentilesAtom,
  radarTableMetricPercentilesAtom,
  radarTemplateIdAtom,
  radarTemplatePositionGroupAtom,
} from '@/atoms/radar';
import { GeneralFallback } from '@/components/contentState/GeneralFallback';
import { MAX_PLAYER_BIO_ROW_COUNT, usePlayerBioData } from '@/hooks/radar/usePlayerBioData';
import { useRadar360UnavailableMetricKeys } from '@/hooks/useRadar360UnavailableMetricKeys';
import { createPlaceholderData } from './RadarBioTable';
import { RadarTableBase, RadarTableDisplayProps } from './RadarTable';

const PlayerRadarTableWithData = ({ isInteractive, showSpokes }: RadarTableDisplayProps) => {
  const radarPercentiles = useAtomValue(radarTableMetricPercentilesAtom);
  const comparisonPercentiles = useAtomValue(radarTableComparisonMetricPercentilesAtom);
  const radarTemplateId = useAtomValue(radarTemplateIdAtom);
  const templates = useAtomValue(radarTemplatesAtom);
  const unavailable360MetricKeys = useRadar360UnavailableMetricKeys();
  const loadableRadarData = useAtomValue(loadablePlayerRadarDataAtom);
  const isComparisonDataLoading = useAtomValue(playerComparisonIsLoadingAtom);
  const isLeagueAverageLoading = useAtomValue(playerLeagueAverageIsLoadingAtom);
  const isFetchingPercentiles =
    loadableRadarData.state === 'loading' || isComparisonDataLoading || isLeagueAverageLoading;

  const metricData = useAtomValue(playerMetricsAtom);
  const minutesPlayed = useAtomValue(unwrappedPlayerAggsMinutesPlayedAtom);
  const minutesPlayed360 = useAtomValue(unwrappedPlayerAggsMinutesPlayed360Atom);
  const comparisonMinutesPlayed = useAtomValue(unwrappedPlayerRenderedComparisonMinutesPlayedAtom);
  const comparisonMinutesPlayed360 = useAtomValue(unwrappedPlayerRenderedComparisonMinutesPlayed360Atom);

  const { bioData, entityName, comparisonEntityName } = usePlayerBioData();

  return (
    <RadarTableBase
      section="player"
      radarTemplateId={radarTemplateId}
      isInteractive={isInteractive}
      radarPercentiles={radarPercentiles}
      isLoading={isFetchingPercentiles}
      templates={templates}
      unavailable360MetricKeys={unavailable360MetricKeys}
      metricData={metricData}
      minutesPlayed={minutesPlayed}
      minutesPlayed360={minutesPlayed360}
      comparisonPercentiles={comparisonPercentiles}
      comparisonMinutesPlayed={comparisonMinutesPlayed}
      comparisonMinutesPlayed360={comparisonMinutesPlayed360}
      entityName={entityName}
      bioData={bioData}
      comparisonEntityName={comparisonEntityName}
      showSpokes={showSpokes}
    />
  );
};

export const PlayerRadarTable = ({ isInteractive, showSpokes }: RadarTableDisplayProps) => (
  <ScopeProvider atoms={[metricsAtom, radarTemplatePositionGroupAtom]}>
    <ErrorBoundary fallback={<GeneralFallback type="error" />}>
      <Suspense
        fallback={
          <RadarTableBase
            section="player"
            isLoading
            isInteractive={isInteractive}
            bioData={createPlaceholderData(MAX_PLAYER_BIO_ROW_COUNT)}
            showSpokes={showSpokes}
          />
        }
      >
        <PlayerRadarTableWithData isInteractive={isInteractive} showSpokes={showSpokes} />
      </Suspense>
    </ErrorBoundary>
  </ScopeProvider>
);
